import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import SmartStatCard from '../../../components/ui/SmartStatCard';
import { useHrAnalyticsApi } from '../../hr-analytics/config';

// Görev 13 — ana dashboard'a akıllı istatistik satırı (spec §13.1, Karar 5): "HA'sı olmayan
// müşterinin dashboard'u hiç değişmez." Kilitli kart YOK, bulanık önizleme YOK, "yükselt"
// teaser'ı YOK, boş bir slot bile YOK.
//
// hasHaModule DIŞARIDAN (DashboardPage) gelir — orada resolveHasHaModule({ hasModule, canRead })
// ile hesaplanır (bkz. dashboard/resolveHasHaModule.js). Fix round 1 (review): yalnız canRead('HA')
// YETERSİZDİ — AuthContext.hasPermission enterpriseAdmin için koşulsuz true döner, tenant'ın HA
// aboneliği olup olmadığına bakmadan; bu yüzden hasModule('HA') (tenant aboneliği, kullanıcı
// ayrıcalığından BAĞIMSIZ) ile birleştirildi. Bileşen kendi başına AuthContext'e bakıp ikinci bir
// "modül var mı" yolu İCAT ETMEZ — dışarıdan gelen (artık doğru) tek bir değeri kullanır.
//
// false iken: hook'lar (Kuralları gereği) yine koşulsuz çağrılır, ama effect gövdesi API
// çağrısından ÖNCE çıkar ve JSX null döner — container'da tek bir DOM düğümü bile kalmaz. Bu,
// görevin TEK en önemli gereksinimidir; her iki yarısı da (render YOK + istek YOK) ayrı ayrı
// test edilir. DashboardPage'deki `{hasHaModule && <SmartStatsRow hasHaModule={hasHaModule} />}`
// sarmalayıcısı ile bu bileşenin kendi kontrolü AYNI değeri okur — biri diğerinden BAĞIMSIZ bir
// doğrulama değildir (yalnız DashboardPage'in ileride bu satırı koşulsuz mount etmesine karşı bir
// güvence); flag'in kendisi (resolveHasHaModule) yanlış hesaplanırsa ikisi de aynı anda yanılır.
//
// ── Seçilen dört metrik ──────────────────────────────────────────────────────────────────────
// Brief'in EM-only listesinden bir yöneticinin ÜZERİNE hemen hareket edeceği TEKİL sayılar
// (dağılım/eğri/funnel DEĞİL):
//   headcount.active       → o anki toplam aktif kadro (en temel referans sayısı)
//   turnover.total.rate     → toplam devir oranı (klasik, aylık izlenen KPI)
//   retention.rate.12m      → 12 aylık elde tutma oranı (devrin doğal tamamlayıcısı)
//   headcount.vacancy.rate  → bütçeye göre açık pozisyon oranı (personel planlama sinyali)
//
// Dışarıda bırakılanlar (bilinçli karar, brief'in izin verdiği gibi burada gerekçelendirilir):
//   survival.curve.tenure / turnover.byScope.funnel — brief'in kendi notu: eğri/karşılaştırma
//     metrikleri (HaSurvivalPointResponse bir NOKTA DİZİSİ, HaFunnelCellResponse kontrol
//     limitli bir karşılaştırma), SmartStatCard'ın tek-değerli sözleşmesine sığmaz.
//   demographics.* / headcount.byEmploymentType / .byCollarType — teknik olarak tekil değerli
//     (KONSANTRASYON: "en büyük grubun payı", bkz. HeadcountConcentrationComputerBase /
//     DemographicsConcentrationComputerBase) ama soyut bir istatistik; ham dağılımı kullanıcı
//     zaten DepartmentDistributionCard/AgeDistributionCard'ta görüyor. Bu dört metrik kadar
//     "üzerine hemen aksiyon alınır" değil — Genel Bakış'ın (Görev 16) metrik kataloğuna daha uygun.
//   turnover.newHireFailure.90d — NewHireFailure90dComputer'ın kendi yorumunun uyardığı gibi
//     gösterilen ay o aydaki işe alımları DEĞİL, yaklaşık ÜÇ AY ÖNCESİNİN kohortunu anlatır (90
//     günlük bir sonuç 90 gün dolmadan bilinemez). SmartStatCard'ı DEĞİŞTİRMEDEN (Görev 12,
//     zaten review'dan geçmiş) bu gecikmeyi yanıltmadan tek bir kompakt etikete sığdırmanın
//     güvenli bir yolu yoktu — bu yüzden bilinçli olarak bu satırdan ÇIKARILDI.
//
// "Temel" (basis) çipi bilinçli olarak GEÇİLMEDİ: HaMetricCellResponse (GET /v1/ha/metrics'in
// hücre şekli) basis alanı TAŞIMAZ — o yalnız /catalog'dadır. Bunun için ikinci bir API çağrısı
// eklemek bu kompakt satırın "tek, ucuz okuma" amacını aşardı; "Temel" zaten Genel Bakış'ın
// metrik kataloğunda gösteriliyor.
const METRIC_CODES = [
  'headcount.active',
  'turnover.total.rate',
  'retention.rate.12m',
  'headcount.vacancy.rate',
];

// Çeviri anahtarı eşlemesi bilinçli olarak DÜZ (nokta'sız) camelCase: responseErrors.ha.*
// desenindeki gibi kod noktalarını iç içe i18next anahtarlarına çevirmek (ör.
// "retention.rate.12m" → dashboardMetrics.retention.rate['12m']) hem çirkin hem kırılgan
// olurdu. Burada sabit, küçük, tamamen istemci tarafında sahip olunan bir kod kümesi olduğundan
// düz bir eşleme daha güvenli ve daha okunur.
const METRIC_LABEL_KEYS = {
  'headcount.active': 'headcountActive',
  'turnover.total.rate': 'turnoverTotalRate',
  'retention.rate.12m': 'retentionRate12m',
  'headcount.vacancy.rate': 'headcountVacancyRate',
};

const CODES_PARAM = METRIC_CODES.join(',');

// Kart artık kendi genişliğini BELİRLEMEZ — bulunduğu `auto-fit` ızgarasında bir hücredir.
// (Eskiden `span 3` taşıyordu; dört metrikten biri kilitliyse satır delik kalıyordu.)
const GridItem = styled.div`
  min-width: 0;
`;

// HaMetricsDataAccess.GetMetricsAsync hücreleri PeriodStart'a göre ARTAN sırada döner
// (.OrderBy(x => x.PeriodStart)) — "şu anki" değer dizinin SON elemanıdır, ilki DEĞİL.
function latestCell(cells) {
  if (!Array.isArray(cells) || cells.length === 0) return null;
  return cells[cells.length - 1];
}

export default function SmartStatsRow({ hasHaModule }) {
  const { t } = useTranslation();
  const { getMetrics } = useHrAnalyticsApi();
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!hasHaModule) return undefined; // Karar 5: HA yoksa boşuna istek dahi atma.

    let cancelled = false;
    getMetrics({ codes: CODES_PARAM, scope: 'tenant', grain: 'month' }).then((res) => {
      if (!cancelled) setResults(res?.data?.data ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [hasHaModule, getMetrics]);

  // Her kod için: kilitli/henüz kullanılamayan metrik (ör. insufficient_history) satırdan
  // SESSİZCE düşer — SmartStatCard'ın bir "kilitli" gösterimi yok (yalnız k-anonimlik bastırması
  // var), ve bu kompakt satırın amacı tam-hazır sayılar göstermektir; şeffaflık zaten Genel
  // Bakış'ın metrik kataloğunda mevcut. Hücre listesi boşsa (henüz hiç snapshot üretilmemiş) da
  // aynı şekilde atlanır — "0" ya da tire UYDURULMAZ.
  const cards = useMemo(() => {
    if (!results) return [];
    const byCode = Object.fromEntries(results.map((r) => [r.code, r]));

    return METRIC_CODES.map((code) => {
      const result = byCode[code];
      if (!result || result.state !== 'available') return null;
      const cell = latestCell(result.cells);
      if (!cell) return null;
      return { code, value: cell.value, suppressedReason: cell.suppressedReason };
    }).filter(Boolean);
  }, [results]);

  if (!hasHaModule) return null;
  if (cards.length === 0) return null;

  return cards.map((card) => (
    <GridItem key={card.code}>
      <SmartStatCard
        label={t(`hrAnalytics.dashboardMetrics.${METRIC_LABEL_KEYS[card.code]}`)}
        value={card.value}
        suppressedReason={card.suppressedReason}
      />
    </GridItem>
  ));
}
